import React, { useCallback, useEffect, useMemo } from "react";

import { withStyles } from "@mui/styles";
import trim from "lodash/trim";
import { FormattedMessage } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";

import {
  checkResetKey,
  changeLanguage,
  setMailSent,
  saveNewPassword,
  resetError,
} from "../../actions/reset.actions";

import { checkEmailExists } from "../../../../shared/api/gateway/accounts/accounts.api";
import { EntryContainer } from "../../../../shared/containers/EntryContainer/EntryContainer";
import EmailEntryForm from "../../components/EmailEntryForm";
import NewPasswordForm from "../../components/NewPasswordForm";
import { AnyTodo } from "@/types";

// --- styles ---
const style = {
  textField: {
    textAlign: "left",
    width: "100%",
    marginTop: 0,
  },
  header: {
    marginTop: 0,
  },
  button: {
    display: "block",
    textAlign: "center",
    width: "100%",
  },
  simpleMessage: {
    textAlign: "center",
    marginTop: 16,
    marginBottom: 16,
  },
} as const;

type Classes = Record<keyof typeof style, string>;

// --- redux ---
const mapStateToProps = (state: AnyTodo) => ({
  isFetching: state.ui.reset.isFetching,
  mailSent: state.ui.reset.mailSent,
  resetKeyValid: state.ui.reset.resetKeyValid,
  resetSuccess: state.ui.reset.resetSuccess,
  error: state.ui.reset.error,
});

const mapDispatchToProps = (dispatch: AnyTodo) =>
  bindActionCreators(
    {
      checkResetKey,
      changeLanguage,
      setMailSent,
      saveNewPassword,
      resetError,
      checkEmailExists,
    },
    dispatch,
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

// withStyles přidá classes
type Props = ReduxProps & { classes: Classes };

const Reset: React.FC<Props> = (props) => {
  const {
    classes,
    isFetching = false,
    mailSent,
    resetKeyValid,
    resetSuccess,
    error = null,
    checkResetKey: checkResetKeyAction,
    setMailSent: setMailSentAction,
    saveNewPassword: saveNewPasswordAction,
    resetError: resetErrorAction,
    checkEmailExists: checkEmailExistsAction,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const gwUrl = process.env.GATEWAY_URL as string | undefined;

  const resetKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("key"); // string | null
  }, [location.search]);

  // on mount / when key changes: validate reset key (if present)
  useEffect(() => {
    if (!gwUrl) return;
    if (resetKey) {
      checkResetKeyAction(gwUrl, resetKey);
    }
  }, [gwUrl, resetKey, checkResetKeyAction]);

  // redirect on success
  useEffect(() => {
    if (resetSuccess) {
      navigate("/login");
    }
  }, [resetSuccess, navigate]);

  const submitEmailReset = useCallback(
    (email: string) => {
      if (!gwUrl) return;
      setMailSentAction(gwUrl, trim(email));
    },
    [gwUrl, setMailSentAction],
  );

  const submitNewPassword = useCallback(
    (pass: string) => {
      if (!gwUrl) return;
      if (!resetKey) return; // bez klíče nemá smysl volat saveNewPassword
      saveNewPasswordAction(gwUrl, resetKey, trim(pass));
    },
    [gwUrl, resetKey, saveNewPasswordAction],
  );

  const renderWithKey = () => {
    if (resetKeyValid) {
      return (
        <NewPasswordForm
          onNewPassword={(pass: string) => submitNewPassword(pass)}
        />
      );
    }

    return (
      <div className={classes.simpleMessage} id="invalid-key">
        <FormattedMessage id="reset.key.not.valid" />
      </div>
    );
  };

  const renderWithoutKey = () => {
    if (mailSent) {
      return (
        <>
          <div className={classes.simpleMessage} id="email-sent-message">
            <FormattedMessage id="reset.mail.sent.message" />
          </div>
          <Link className={classes.button} color="primary" to="/login">
            <FormattedMessage id="reset.linkToLogin" />
          </Link>
        </>
      );
    }

    return (
      <EmailEntryForm
        checkEmailExists={checkEmailExistsAction}
        errorAction={resetErrorAction}
        submitEmailReset={submitEmailReset}
      />
    );
  };

  const contentRender = () => {
    if (error === null) {
      return !resetKey ? renderWithoutKey() : renderWithKey();
    }

    return (
      <div className={classes.simpleMessage}>
        <FormattedMessage id="reset.error" />
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <a href="mailto:help@cleverfarm.cz">help@cleverfarm.cz</a>
      </div>
    );
  };

  return (
    <EntryContainer isFetching={isFetching}>{contentRender()}</EntryContainer>
  );
};

export default connector(withStyles(style)(Reset));
